/// Les cibles sur lesquelles le même renderer est servi.
///
/// `web` : navigateur, y compris installé depuis l'écran d'accueil.
/// `desktop` : l'application Electron.
/// `ios` / `android` : la WebView native empaquetée par Capacitor.
export type AppTarget = 'web' | 'desktop' | 'ios' | 'android';

/// Ce que le build a produit. Injecté à la compilation, jamais deviné : c'est
/// la seule information fiable. `window.electron` a longtemps servi à ça et se
/// trouve être truthy dans le conteneur Docker de développement — l'application
/// s'y croyait installée alors qu'elle tournait dans un navigateur (voir
/// LESSONS.md). Une valeur figée au build ne peut pas mentir.
type BuildTarget = 'web' | 'desktop' | 'native';

function resolveTarget(): AppTarget {
  const build: BuildTarget =
    typeof __APP_TARGET__ === 'string'
      ? (__APP_TARGET__ as BuildTarget)
      : 'web';

  // `window` manque quand les tests chargent ce module sous Node.
  const electron = typeof window !== 'undefined' && !!window.electron;
  if (electron || build === 'desktop') return 'desktop';

  if (build === 'native') {
    // Un seul bundle sert iOS et Android : seul l'hôte peut les départager.
    const platform =
      typeof window !== 'undefined'
        ? window.Capacitor?.getPlatform?.()
        : undefined;
    return platform === 'android' ? 'android' : 'ios';
  }

  return 'web';
}

export const TARGET: AppTarget = resolveTarget();

export const IS_WEB = TARGET === 'web';
export const IS_DESKTOP = TARGET === 'desktop';
export const IS_NATIVE = TARGET === 'ios' || TARGET === 'android';
export const IS_IOS = TARGET === 'ios';
export const IS_ANDROID = TARGET === 'android';

/// Vrai dans le bundle embarqué par Capacitor, connu **à la compilation**.
///
/// `IS_NATIVE` ci-dessus se résout à l'exécution, puisqu'il faut interroger
/// l'hôte pour distinguer iOS d'Android. Une constante d'exécution empêche Vite
/// de supprimer le code qu'elle ferme : l'interface d'achat Stripe resterait
/// dans le bundle des magasins, simplement inatteignable. Comparée ici à une
/// valeur injectée, l'expression se replie et le code part avec elle.
const IS_NATIVE_BUILD =
  typeof __APP_TARGET__ === 'string' && __APP_TARGET__ === 'native';

/// Ce que la cible autorise, exprimé par intention plutôt que par plateforme.
/// Un appelant qui écrit `CAN_LINK_TO_EXTERNAL_PURCHASE` dit ce qu'il veut
/// faire ; il n'a pas à savoir qu'Apple l'interdit, ni à être relu le jour où
/// une autre cible s'ajoute.
export const CAPABILITIES = {
  /// Ouvrir une page d'achat hors de l'application — Stripe, tarifs, factures.
  /// Interdit dans les magasins : la règle 3.1.1 d'Apple proscrit tout bouton,
  /// lien ou incitation menant à un paiement extérieur, et Google applique une
  /// règle voisine. Sur ces cibles l'abonnement passe par l'achat intégré.
  linkToExternalPurchase: !IS_NATIVE_BUILD,

  /// Acheter via l'achat intégré du magasin : StoreKit sur iOS, Play Billing
  /// sur Android, par le plugin de la coquille.
  purchaseInApp: IS_NATIVE_BUILD,

  /// Télécharger un fichier via un lien `download`. Inerte dans une WebView
  /// iOS : il faut passer par le partage système ou un plugin d'écriture.
  downloadFiles: !IS_NATIVE_BUILD,

  /// Barre de titre dessinée par l'application, boutons fenêtre, mises à jour.
  windowChrome: IS_DESKTOP,

  /// Connexion par un fournisseur tiers (Google, Apple). Dès qu'on en propose
  /// un, la règle 4.8 impose « Se connecter avec Apple » en équivalent. Dans
  /// l'app native, la fenêtre surgissante de Firebase part dans Safari et ne
  /// revient jamais : connexion par e-mail seule.
  thirdPartySignIn: !IS_NATIVE_BUILD,

  /// Transmettre de vrais ordres : terminal, copieur, gestion des positions.
  /// Apple exige une licence financière dans chaque pays de distribution pour
  /// une app qui facilite le trading de CFD ou de forex (règles 3.2.1(viii) et
  /// 3.2.2(viii)) : la version des magasins suit et analyse, sans ordres.
  placeOrders: !IS_NATIVE_BUILD,

  /// Quant Lab : ses jetons de calcul s'achètent hors achat intégré, Kara IA
  /// confie les données à un tiers, et l'exécution trade seule.
  quantLab: !IS_NATIVE_BUILD,

  /// Montrer des contenus publiés que personne n'a relus (communautés non
  /// vérifiées) : la règle 1.2 exige un filtre avant publication.
  listUnmoderatedContent: !IS_NATIVE_BUILD,

  /// Liaisons OAuth qui quittent l'application (Discord, cTrader) : dans
  /// l'app native, le retour n'y revient jamais.
  externalAccountLinking: !IS_NATIVE_BUILD,

  /// Programme d'affiliation : coordonnées bancaires et liens de parrainage
  /// vers le site.
  affiliation: !IS_NATIVE_BUILD,

  /// Annonces de l'équipe en fenêtre au lancement : écrites pour le web et le
  /// bureau (notes de version, fonctions absentes de l'app des magasins), sans
  /// ciblage par plateforme.
  announcements: !IS_NATIVE_BUILD,
} as const;

export type Capability = keyof typeof CAPABILITIES;
